import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
// import { FallbackImage } from './FallbackImage';

interface Screenshot {
  src: string;
  webpSrc?: string;
  alt: string;
  title: string;
  description?: string;
}

interface ScreenshotGalleryProps {
  screenshots: Screenshot[];
}

interface LightboxProps {
  screenshots: Screenshot[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({
  screenshots,
  currentIndex,
  isOpen,
  onClose,
  onNext,
  onPrevious,
}) => {
  const lightboxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrevious();
          break;
        case 'ArrowRight':
          onNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNext, onPrevious]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const currentScreenshot = screenshots[currentIndex];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={lightboxRef}
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={(e) => {
            if (e.target === lightboxRef.current) {
              onClose();
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="lightbox-title"
          aria-describedby="lightbox-description"
        >
          <motion.div 
            className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Close button */}
            <motion.button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 transition-colors p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black touch-manipulation"
              aria-label="Close image viewer"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              autoFocus
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </motion.button>

            {/* Previous button */}
            {screenshots.length > 1 && (
              <motion.button
                onClick={onPrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gray-300 transition-colors p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black touch-manipulation"
                aria-label={`Previous image (${currentIndex} of ${screenshots.length})`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.1, x: -2 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronLeftIcon className="h-8 w-8" aria-hidden="true" />
              </motion.button>
            )}

            {/* Next button */}
            {screenshots.length > 1 && (
              <motion.button
                onClick={onNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gray-300 transition-colors p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black touch-manipulation"
                aria-label={`Next image (${currentIndex + 2} of ${screenshots.length})`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.1, x: 2 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronRightIcon className="h-8 w-8" aria-hidden="true" />
              </motion.button>
            )}

            {/* Image */}
            <div className="flex flex-col items-center justify-center max-w-full max-h-full">
              <motion.picture 
                className="max-w-full max-h-[80vh] flex items-center justify-center"
                key={currentIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                {currentScreenshot.webpSrc && (
                  <source srcSet={currentScreenshot.webpSrc} type="image/webp" />
                )}
                <img
                  src={currentScreenshot.src}
                  alt={currentScreenshot.alt}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  loading="eager"
                  role="img"
                  aria-describedby="lightbox-description"
                />
              </motion.picture>
              
              {/* Image info */}
              <motion.div 
                className="mt-4 text-center text-white max-w-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                <h3 id="lightbox-title" className="text-xl font-semibold mb-2">{currentScreenshot.title}</h3>
                {currentScreenshot.description && (
                  <p id="lightbox-description" className="text-gray-300">{currentScreenshot.description}</p>
                )}
                {screenshots.length > 1 && (
                  <p className="text-sm text-gray-400 mt-2" aria-live="polite">
                    Image {currentIndex + 1} of {screenshots.length}
                  </p>
                )}
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface LazyImageProps {
  screenshot: Screenshot;
  onClick: () => void;
  className?: string;
  index: number;
}

const LazyImage: React.FC<LazyImageProps> = ({ screenshot, onClick, className = '', index }) => {
  return (
    <motion.button
      className={`relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full min-h-[200px] ${className}`}
      onClick={onClick}
      whileHover={{ 
        y: -8,
        scale: 1.02,
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      role="listitem"
      aria-label={`View ${screenshot.title} in full size. ${screenshot.description || ''}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <img
        src={screenshot.src}
        alt={screenshot.alt}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        loading="lazy"
        onError={(error) => {
          console.error('Screenshot failed to load:', screenshot.title, error);
          // Set a placeholder background color on error
          const target = error.target as HTMLImageElement;
          target.style.backgroundColor = '#f3f4f6';
          target.style.display = 'flex';
          target.style.alignItems = 'center';
          target.style.justifyContent = 'center';
          target.alt = 'Image unavailable';
        }}
        onLoad={() => {
          console.log('Screenshot loaded successfully:', screenshot.title);
        }}
        role="img"
        aria-describedby={`screenshot-desc-${index}`}
      />

      {/* Overlay */}
      <motion.div 
        className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
      >
        <motion.div 
          className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center p-4"
          initial={{ y: 20, opacity: 0 }}
          whileHover={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
          aria-hidden="true"
        >
          <h3 className="font-semibold text-lg mb-1">{screenshot.title}</h3>
          {screenshot.description && (
            <p id={`screenshot-desc-${index}`} className="text-sm text-gray-200">{screenshot.description}</p>
          )}
        </motion.div>
      </motion.div>
    </motion.button>
  );
};

export const ScreenshotGallery: React.FC<ScreenshotGalleryProps> = ({ screenshots }) => {
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % screenshots.length);
  };

  const previousImage = () => {
    setLightboxIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  if (!screenshots || screenshots.length === 0) {
    return null;
  }

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  return (
    <section 
      className="py-16 sm:py-20 bg-gray-50"
      aria-labelledby="screenshots-heading"
      role="region"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12"
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2 
            id="screenshots-heading"
            className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            variants={headerVariants}
          >
            See the Platform in Action
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto"
            variants={headerVariants}
            aria-describedby="screenshots-heading"
          >
            Explore our intuitive interface and powerful analytics tools that help you track 
            and improve your bowling performance.
          </motion.p>
        </motion.div>

        {/* Screenshot Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 auto-rows-fr"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          role="list"
          aria-label="Platform screenshots gallery"
        >
          {screenshots.map((screenshot, index) => (
            <LazyImage
              key={index}
              screenshot={screenshot}
              onClick={() => openLightbox(index)}
              className="aspect-video bg-gray-100"
              index={index}
            />
          ))}
        </motion.div>

        {/* Additional Info */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <p className="text-gray-600">
            Click any image to view it in full size
          </p>
        </motion.div>
      </div>

      {/* Lightbox */}
      <Lightbox
        screenshots={screenshots}
        currentIndex={lightboxIndex}
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrevious={previousImage}
      />
    </section>
  );
};

export default ScreenshotGallery;